// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Matching a pasted "expected" checksum against the digests we computed.
 * Pure and thoroughly tested — this is the trust-critical comparison.
 */
import { algosByHexLength } from './algorithms';
import type { HashSet, VerifyResult } from './types';

/**
 * Normalise a pasted expected checksum. Handles the shapes people actually
 * copy: a bare hex digest, an uppercase digest, a `sha256:<hex>` prefix, and a
 * `<hex>  filename.iso` line straight out of a `SHA256SUMS` file (including the
 * BSD `SHA256 (file) = <hex>` form).
 */
export function normalizeExpected(raw: string): string {
  let s = raw.trim();
  if (s === '') return '';

  // BSD-style: "SHA256 (file.iso) = abcd..."  → take the part after '='.
  const eq = s.lastIndexOf('=');
  if (eq !== -1 && /\)\s*=/.test(s)) s = s.slice(eq + 1);

  // "algo:hexdigest" prefix.
  const colon = s.indexOf(':');
  if (colon !== -1 && /^[A-Za-z0-9_-]+$/.test(s.slice(0, colon))) {
    s = s.slice(colon + 1);
  }

  // "hexdigest  filename" (GNU coreutils) → the first whitespace-delimited token.
  s = s.trim().split(/\s+/)[0] ?? '';

  // Drop a leading '*' binary-mode marker some tools emit, then lowercase.
  s = s.replace(/^\*/, '').toLowerCase();
  return s;
}

/** True only if the whole string is hex of a length we recognise. */
export function isHexDigest(s: string): boolean {
  return /^[0-9a-f]+$/.test(s) && s.length % 2 === 0;
}

/** Constant-time-ish equality for two equal-length hex strings. */
export function hexEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/**
 * Compare the expected checksum against a set of computed digests.
 * `computed` may be partial (some algorithms not yet finished/enabled).
 */
export function verifyExpected(rawExpected: string, computed: HashSet): VerifyResult {
  const normalized = normalizeExpected(rawExpected);
  if (normalized === '') {
    return { status: 'idle', candidates: [], normalized };
  }
  if (!isHexDigest(normalized)) {
    return { status: 'unknown-length', candidates: [], normalized };
  }

  const candidates = algosByHexLength(normalized.length);
  if (candidates.length === 0) {
    return { status: 'unknown-length', candidates: [], normalized };
  }

  // A match against any candidate algorithm we have a digest for wins.
  for (const algo of candidates) {
    const digest = computed[algo];
    if (digest && hexEqual(digest, normalized)) {
      return { status: 'match', matchedAlgo: algo, candidates, normalized };
    }
  }

  // Do we even have a computed digest for any candidate yet?
  const haveAny = candidates.some((a) => typeof computed[a] === 'string');
  if (!haveAny) {
    return { status: 'pending', candidates, normalized };
  }
  return { status: 'mismatch', candidates, normalized };
}
